import { indexesToMask, maskToIndexes } from '../../utils/maskUtils';

/**
 * Areas are authored as named contiguous ranges ("tables 1-13") but stored as masks. These convert
 * between the two, and detect the masks that no range can describe.
 *
 * Ranges are presented 1-based because that is how staff count tables; indexes stay 0-based.
 */
export type AreaRange = { fromIndex: number; toIndex: number };

export const rangeToMask = (range: AreaRange, resourceCount: number): string => {
  const from = Math.max(0, Math.min(range.fromIndex, range.toIndex));
  const to = Math.min(resourceCount - 1, Math.max(range.fromIndex, range.toIndex));
  const indexes: number[] = [];
  for (let index = from; index <= to; index++) {
    indexes.push(index);
  }
  return indexesToMask(indexes, resourceCount);
};

/**
 * The range a mask describes, or null when its bits are not one unbroken run — a mask built by
 * hand or left over from an earlier config can be anything, and we must not silently rewrite it.
 */
export const maskToRange = (mask: string): AreaRange | null => {
  const indexes = maskToIndexes(mask);
  if (indexes.length === 0) {
    return null;
  }
  const from = indexes[0];
  const to = indexes[indexes.length - 1];
  return to - from + 1 === indexes.length ? { fromIndex: from, toIndex: to } : null;
};

export type RangeGuard = 'out_of_bounds' | 'inverted';

export const validateRange = (range: AreaRange, resourceCount: number): RangeGuard | null => {
  if (range.toIndex < range.fromIndex) {
    return 'inverted';
  }
  if (range.fromIndex < 0 || range.toIndex > resourceCount - 1) {
    return 'out_of_bounds';
  }
  return null;
};

/** Area ids that share at least one resource with another area — a warning, never a blocker (§9). */
export const overlappingAreaIds = (areas: { id: string; mask: string }[]): string[] => {
  const overlapping = new Set<string>();
  areas.forEach((area, index) => {
    const indexes = new Set(maskToIndexes(area.mask));
    areas.slice(index + 1).forEach((other) => {
      if (maskToIndexes(other.mask).some((resource) => indexes.has(resource))) {
        overlapping.add(area.id);
        overlapping.add(other.id);
      }
    });
  });
  return [...overlapping];
};

/** Highest resource index any area or page mask refers to — the floor for shrinking a config. */
export const highestUsedIndex = (masks: string[]): number =>
  masks.reduce((highest, mask) => {
    const indexes = maskToIndexes(mask);
    return indexes.length === 0 ? highest : Math.max(highest, indexes[indexes.length - 1]);
  }, -1);
